/*
 * varify-client-assign.js
 * ---------------------------------------------------------------------------
 * CLIENT-ID A/B ATTRIBUTION (companion to varify-order-stamp.js).
 *
 * WHY: the cart-attribute stamp (varify-order-stamp.js) misses ~20-25% of
 * experiment orders — express / Shop Pay / "Buy it now" / Recharge / cross-
 * session checkouts never re-write the cart attribute. This snippet closes that
 * gap the same way GA4 does: it records which arm a VISITOR (Shopify's
 * _shopify_y visitor token) was bucketed into, so a checkout_completed Web Pixel
 * can join {order -> clientId -> arm} server-side regardless of checkout path.
 *
 * FLOW:
 *   storefront (here):  POST {clientId=_shopify_y, experiment, arm}  -> /attr/assign
 *   Web Pixel:          POST {clientId, orderId}                     -> /attr/order
 *   backend:            join clientId -> arm, attribute the order
 *
 * Reads the SAME Varify assignment sources as varify-order-stamp.js
 * (localStorage "varify-experiment-<id>" + window.varify.appliedExperiments), so
 * it stays in lock-step with that script. Sends no PII — only the anonymous
 * Shopify visitor token + experiment/arm ids. Best-effort, fire-and-forget.
 *
 * Endpoint + key mirror the email-gate / A/B backend on Railway. The key is a
 * light anti-junk barrier only (it ships in client code, like the gate secret).
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var ENDPOINT = 'https://trial-email-gate-production.up.railway.app/attr/assign';
  var KEY = '9103b747a890c7470246ad39e1ac8f69112bc3e8c34ed471';

  // DUAL-POST during the Echo migration. The A/B dashboard has been ported to
  // Echo and both systems are reconciled nightly against each other, so both
  // need the same assignments. Post to each independently: neither is allowed
  // to break the other, and rollback is deleting one of these two lines.
  //
  // Echo authenticates with the client's public site_key rather than a shared
  // secret. That is not a downgrade - the key carries the tenant, so a request
  // cannot land against the wrong brand, and it grants nothing but "record an
  // assignment for this client". The legacy KEY above is a global shared secret
  // with no tenant at all.
  //
  // Remove this block, and the Railway endpoint above, once the Node service is
  // decommissioned (§10 P8).
  var ECHO_ENDPOINT = 'https://api.echo.rawlings.us/api/ab/attr/assign';
  var ECHO_SITE_KEY = 'site_9Gh9ce7x-bcXdZeZr0bvcw';

  // TRIGGERED DENOMINATOR (cart-drawer tests).
  // /attr/assign counts every bucketed visitor, but a cart test only actually
  // reaches the ones who OPEN the cart. Posting here marks a visitor as exposed,
  // so Echo can compute orders / cart-openers rather than orders / all-visitors.
  //
  // Echo-only: the legacy Node service has no equivalent endpoint, and is being
  // decommissioned anyway (§10 P8).
  //
  // ⚠️ THIS MUST LIVE IN THEME CODE, NOT IN A VARIFY VARIATION. If it shipped
  //    inside a variation, Control would never fire it and only the treated arms
  //    would have a denominator — the comparison would be meaningless.
  //
  // ⚠️ FIRST OPEN ONLY. Whether a visitor opens the cart at all is unaffected by
  //    the arm (we only change the drawer once it is already open), which is what
  //    makes this a legitimate denominator. RE-opens are not — a roomier cart
  //    could plausibly change how often people come back to it — so counting
  //    every open would put a treatment-affected number underneath the ratio.
  //    Echo enforces write-once server-side; the sessionStorage guard below just
  //    saves the redundant requests.
  var ECHO_TRIGGER_ENDPOINT = 'https://api.echo.rawlings.us/api/ab/attr/trigger';
  var TRIGGER_SS_KEY = 'np_cart_trigger_sent';
  var RECHARGE_DRAWER_ID = 'recharge-storefront-experiences__cart-drawer';

  // Shopify's anonymous visitor token — equals the Web Pixel event.clientId,
  // which is how the two sides join server-side.
  function shopifyClientId() {
    var m = document.cookie.match(/(?:^|;\s*)_shopify_y=([^;]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }

  // Current Varify assignments (expId -> variationId | "Original").
  function assignments() {
    var out = {};
    try {
      for (var i = 0; i < window.localStorage.length; i++) {
        var k = window.localStorage.key(i);
        if (!k || k.indexOf('varify-experiment-') !== 0 || k.indexOf('exclusion') !== -1) continue;
        var exp = k.slice('varify-experiment-'.length);
        if (!exp) continue;
        var raw = window.localStorage.getItem(k), arm = raw;
        try {
          var p = JSON.parse(raw);
          if (p && typeof p === 'object' && 'variationId' in p) {
            arm = p.variationId == null ? 'Original' : String(p.variationId);
          }
        } catch (e) { /* non-JSON raw value — keep as-is */ }
        if (arm != null && arm !== '') out[exp] = String(arm);
      }
    } catch (e) { /* localStorage blocked */ }
    try {
      var applied = window.varify && window.varify.appliedExperiments;
      if (applied) {
        Object.keys(applied).forEach(function (exp) {
          var v = applied[exp] && applied[exp].variationId;
          if (v != null && out[exp] == null) out[exp] = String(v);
        });
      }
    } catch (e) { /* no runtime state */ }
    return out;
  }

  function send() {
    var cid = shopifyClientId();
    if (!cid) return; // no Shopify visitor token yet
    var a = assignments();
    var exps = Object.keys(a);
    if (!exps.length) return; // not bucketed into anything yet
    var sent = window.__abAssignSent || (window.__abAssignSent = {});
    exps.forEach(function (exp) {
      var mark = cid + ':' + exp + ':' + a[exp];
      if (sent[mark]) return; // send each assignment once per page load
      sent[mark] = 1;
      try {
        fetch(ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          keepalive: true,
          body: JSON.stringify({ key: KEY, clientId: cid, experiment: exp, arm: a[exp] }),
        }).catch(function () {});
      } catch (e) { /* fire-and-forget */ }
      // Echo. Separate try/catch and its own .catch so a failure here cannot
      // affect the call above, and neither can affect the page.
      try {
        fetch(ECHO_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Site-Key': ECHO_SITE_KEY },
          keepalive: true,
          body: JSON.stringify({ clientId: cid, experiment: exp, arm: a[exp] }),
        }).catch(function () {});
      } catch (e) { /* fire-and-forget */ }
    });
  }

  // ---- cart-drawer trigger ------------------------------------------------

  var cartOpened = false;

  function alreadySent(cid, exp) {
    try {
      var raw = window.sessionStorage.getItem(TRIGGER_SS_KEY);
      return !!raw && raw.split(',').indexOf(cid + ':' + exp) !== -1;
    } catch (e) { return false; }
  }

  function markSent(cid, exp) {
    try {
      var raw = window.sessionStorage.getItem(TRIGGER_SS_KEY) || '';
      window.sessionStorage.setItem(TRIGGER_SS_KEY, raw ? raw + ',' + cid + ':' + exp : cid + ':' + exp);
    } catch (e) { /* sessionStorage blocked — Echo still de-dupes server-side */ }
  }

  // Post once per (visitor, experiment). No `arm` in the payload: Echo already
  // has it from /attr/assign, and sending it again would create a second source
  // of truth free to disagree with the first.
  function flushTrigger() {
    if (!cartOpened) return;
    var cid = shopifyClientId();
    if (!cid) return;
    var a = assignments();
    Object.keys(a).forEach(function (exp) {
      if (alreadySent(cid, exp)) return;
      markSent(cid, exp);
      try {
        fetch(ECHO_TRIGGER_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Site-Key': ECHO_SITE_KEY },
          keepalive: true,
          body: JSON.stringify({ clientId: cid, experiment: exp }),
        }).catch(function () {});
      } catch (e) { /* fire-and-forget: never let analytics break the cart */ }
    });
  }

  // Recharge's drawer is a React app using `react-remove-scroll`, which sets
  // `data-scroll-locked` on <body> (plus a `block-interactivity-*` class) while
  // its overlay is open. Watching that is far cheaper than reaching into the
  // drawer's shadow root, and it fires whether the drawer was opened by
  // add-to-cart or by the header cart icon. Same technique as
  // assets/custom-buy-box.js, which uses it to hide the sticky bar.
  function watchCart() {
    var check = function () {
      if (cartOpened) return;
      var locked = document.body.hasAttribute('data-scroll-locked') ||
        /(^|\s)block-interactivity-/.test(document.body.className || '');
      // Another React overlay (a Loox lightbox, say) locks scroll the same way,
      // so confirm it is actually the cart before counting an exposure.
      if (!locked || !document.getElementById(RECHARGE_DRAWER_ID)) return;
      cartOpened = true;
      flushTrigger();
    };
    try {
      new MutationObserver(check).observe(document.body, {
        attributes: true, attributeFilter: ['data-scroll-locked', 'class']
      });
    } catch (e) { /* no observer — the initial check below still covers a warm load */ }
    check();
  }

  // Varify applies after load; send shortly after, then retry once for late apply.
  // flushTrigger rides along: the drawer can open before Varify has bucketed the
  // visitor, in which case there was nothing to post at open time.
  function tick() { send(); flushTrigger(); }
  if (document.readyState !== 'loading') { setTimeout(tick, 1200); watchCart(); }
  else document.addEventListener('DOMContentLoaded', function () { setTimeout(tick, 1200); watchCart(); });
  setTimeout(tick, 4000);
})();
